import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { IconChevronDown } from './icons';

export interface NavGroupItem {
  to: string;
  label: string;
  end?: boolean;
  // Overrides react-router's own prefix/exact matching for whether this
  // item is highlighted — needed when a route conceptually belongs to one
  // nav item (e.g. Compensation) but lives under another item's URL prefix
  // (e.g. /hr/employees/:id/pay), so plain prefix matching would light up
  // the wrong one.
  isActive?: (pathname: string) => boolean;
}

// A plain sub-header divider row (e.g. "Revenue" / "Procurement" /
// "Resources" inside the Finance & Accounting group) — has no route of its
// own, so it's a separate shape rather than an optional `header` field
// tacked onto NavGroupItem (which would leave `to`/`label` incorrectly
// required-but-unused on header rows).
export interface NavGroupHeader {
  header: string;
}

export type NavGroupEntry = NavGroupItem | NavGroupHeader;

function isHeader(item: NavGroupEntry): item is NavGroupHeader {
  return 'header' in item;
}

interface NavGroupProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  basePath: string | string[];
  items: NavGroupEntry[];
}

export default function NavGroup({ icon: Icon, label, basePath, items }: NavGroupProps) {
  const location = useLocation();
  const basePaths = Array.isArray(basePath) ? basePath : [basePath];
  // '/' is matched exactly (the Dashboard route) — startsWith('/') would
  // otherwise match every route in the app, permanently marking this group
  // active/expanded regardless of the current page.
  const isActiveRoute = basePaths.some((bp) => (bp === '/' ? location.pathname === '/' : location.pathname.startsWith(bp)));
  const [open, setOpen] = useState(isActiveRoute);

  // Keep the group expanded while inside one of its routes.
  useEffect(() => {
    if (isActiveRoute) setOpen(true);
  }, [isActiveRoute]);

  return (
    <>
      <button
        type="button"
        className={`nav-group-toggle${isActiveRoute ? ' active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <Icon className="nav-icon" />
        {label}
        <IconChevronDown className={`nav-group-chevron${open ? ' open' : ''}`} />
      </button>
      {open && (
        <div className="nav-group">
          {items.map((item) =>
            isHeader(item) ? (
              <div key={item.header} className="nav-group-header">
                {item.header}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => (item.isActive ? (item.isActive(location.pathname) ? 'active' : '') : isActive ? 'active' : '')}
              >
                {item.label}
              </NavLink>
            )
          )}
        </div>
      )}
    </>
  );
}
